// =====================================================
// PLAN CONFIGURATION - Frontend Source
// =====================================================
// NOTE: lib/plans.ts contains the identical config for the API layer.
// Due to CRA import restrictions, we maintain both files.
// ALWAYS keep both files in sync when changing limits!
// =====================================================

export const TRIAL_DAYS = 14;
export const TRIAL_AI_REQUESTS_PER_DAY = 5;
export const TRIAL_RESEARCH_REQUESTS_PER_DAY = 5;

export type PlanType = 'starter' | 'professional' | 'enterprise';

export type SubscriptionStatusType =
  | 'trialing'
  | 'active'
  | 'past_due'
  | 'canceled'
  | 'unpaid'
  | 'incomplete';

export interface PlanFeatures {
  maxProjects: number;
  maxMembers: number;
  maxAiRequestsPerDay: number;
  maxResearchRequestsPerDay: number;
  aiAssistant: boolean;
  aiResearch: boolean;
  advancedReports: boolean;
  apiAccess: boolean;
  customBranding: boolean;
  prioritySupport: boolean;
  sso: boolean;
  dedicatedSupport: boolean;
  customIntegrations: boolean;
  sla: boolean;
}

export interface PlanConfig {
  name: string;
  priceMonthly: number | null;
  priceYearly: number | null;
  features: PlanFeatures;
}

export const PLAN_CONFIGS: Record<PlanType, PlanConfig> = {
  starter: {
    name: 'Starter',
    priceMonthly: 29,
    priceYearly: 290,
    features: {
      maxProjects: 5,
      maxMembers: 3,
      maxAiRequestsPerDay: 50,
      maxResearchRequestsPerDay: 5,
      aiAssistant: true,
      aiResearch: true,
      advancedReports: true,
      apiAccess: false,
      customBranding: false,
      prioritySupport: false,
      sso: false,
      dedicatedSupport: false,
      customIntegrations: false,
      sla: false,
    },
  },
  professional: {
    name: 'Professional',
    priceMonthly: 79,
    priceYearly: 790,
    features: {
      maxProjects: -1,
      maxMembers: 10,
      maxAiRequestsPerDay: 200,
      maxResearchRequestsPerDay: 5,
      aiAssistant: true,
      aiResearch: true,
      advancedReports: true,
      apiAccess: false,
      customBranding: true,
      prioritySupport: true,
      sso: false,
      dedicatedSupport: false,
      customIntegrations: false,
      sla: false,
    },
  },
  enterprise: {
    name: 'Enterprise',
    priceMonthly: null,
    priceYearly: null,
    features: {
      maxProjects: -1,
      maxMembers: -1,
      maxAiRequestsPerDay: -1,
      maxResearchRequestsPerDay: -1,
      aiAssistant: true,
      aiResearch: true,
      advancedReports: true,
      apiAccess: true,
      customBranding: true,
      prioritySupport: true,
      sso: true,
      dedicatedSupport: true,
      customIntegrations: true,
      sla: true,
    },
  },
};

export function getAiDailyLimit(plan: PlanType, status: SubscriptionStatusType): number {
  if (status === 'trialing') return TRIAL_AI_REQUESTS_PER_DAY;
  return PLAN_CONFIGS[plan].features.maxAiRequestsPerDay;
}

export function getResearchDailyLimit(plan: PlanType, status: SubscriptionStatusType): number {
  if (status === 'trialing') return TRIAL_RESEARCH_REQUESTS_PER_DAY;
  return PLAN_CONFIGS[plan].features.maxResearchRequestsPerDay;
}

export function hasResearchAccess(plan: PlanType, status: SubscriptionStatusType): boolean {
  if (status === 'trialing') return true;
  return PLAN_CONFIGS[plan].features.aiResearch;
}

export function isSubscriptionActive(
  status: SubscriptionStatusType,
  trialEndsAt?: string
): boolean {
  if (status === 'active') return true;
  if (status !== 'trialing') return false;
  if (!trialEndsAt) return true;
  return new Date(trialEndsAt).getTime() > Date.now();
}

export function createDefaultSubscription(): {
  plan: PlanType;
  status: SubscriptionStatusType;
  trialEndsAt: string;
  features: PlanFeatures;
} {
  const trialEnd = new Date(Date.now() + TRIAL_DAYS * 24 * 60 * 60 * 1000);
  return {
    plan: 'starter',
    status: 'trialing',
    trialEndsAt: trialEnd.toISOString(),
    features: PLAN_CONFIGS.starter.features,
  };
}

export function isContactUsPlan(plan: PlanType): boolean {
  return PLAN_CONFIGS[plan].priceMonthly === null;
}
